/**
 * Loan tape workflows (DOP-481).
 *
 * A loan tape workflow is a chat of surface `loan_tape` (DOP-477, decision
 * D1) — there is no table behind it. Everything the page shows is therefore
 * read out of the thread: the CONFIG is the question's
 * `message_metadata.schema_extractor`, the RESULT is the answer's
 * `content_blocks`, and a RUN is one question/answer pair. Running it again is
 * an ordinary `stream/start` carrying the same config.
 *
 * The consequence worth naming: the shapes below are a *view* of a chat, not a
 * separate resource. Nothing here writes anything the chat endpoints do not
 * already store, so a workflow and the chat it is made of can never disagree.
 */

import { z } from "zod/v3";
import { ApiError, apiService } from "./api";
import { chatApi } from "./chatApi";
import { apiFetch, apiGet } from "../utils/apiClient";
import { parseContentBlock } from "../components/Chat/Chat.validators";
import type {
	ContentBlock,
	LoanTapePreviewBlock,
	ModifiedWorkbookBlock,
	SchemaGraphBlock,
} from "../components/Chat/Chat.types";
import type { SchemaExtractorPayload } from "../components/SchemaExtractorForm";
import type { CatalogDocument } from "../hooks/useDocumentMention";

/** What the newest generation on a thread did. `null` = it has never run. */
export type LoanTapeRunStatus = "pending" | "running" | "completed" | "failed";

const RUN_STATUSES = ["pending", "running", "completed", "failed"] as const;

/** A run that has not reached a terminal state — Run now stays disabled. */
export const isLoanTapeRunLive = (status: LoanTapeRunStatus | null): boolean =>
	status === "pending" || status === "running";

export interface LoanTapeDashboardSummary {
	/** The chat the extraction streamed into — its id is the workflow's id. */
	id: string;
	name: string;
	/** Epoch ms of the last turn, when there has been one. */
	last_run_ms: number | null;
	/** The newest run's outcome, straight off the list row (DOP-477). */
	status: LoanTapeRunStatus | null;
	/**
	 * Epoch ms the chat was created — `ChatListItem.created_at`, which the chat
	 * list already carries. The index orders a never-run row by it.
	 */
	created_at_ms: number;
}

/** One question/answer pair: a configured run and what it produced. */
export interface LoanTapeRun {
	/** The question message's id — stable, and present before any answer is. */
	id: number;
	started_at_ms: number;
	status: LoanTapeRunStatus;
	/** The config this run was given. `null` for a turn sent from chat prose. */
	config: SchemaExtractorPayload | null;
	/** The answer's blocks. Empty while the run is still streaming. */
	blocks: ContentBlock[];
	/** Why it failed, as the backend wrote it. `null` unless it failed. */
	error: string | null;
}

export interface LoanTapeDashboard {
	id: string;
	title: string;
	/** The newest run's config — what Run now re-sends. */
	config: SchemaExtractorPayload | null;
	/** Newest first. */
	runs: LoanTapeRun[];
	latest: LoanTapeRun | null;
}

// The backend writes an unset optional as `null`, never leaves it out, so
// every optional slot here is `nullish` (DOP-627: `output_filename: null`
// used to fail the whole config, which disabled Run again on every tape
// created without a file name).
const docRefSchema = z.object({
	doc_id: z.string(),
	filename: z.string().nullish().transform((value) => value ?? undefined),
	deal_name: z.string().nullish().transform((value) => value ?? undefined),
	project_id: z.string().nullish(),
});

/**
 * Lenient by design: the stored payload is the raw request dict, and the
 * `filters` block is UI-only and free to grow. Only the three slots the page
 * shows and a re-run needs are pinned; anything else rides along untouched.
 */
const schemaExtractorSchema = z
	.object({
		training_source: docRefSchema,
		training_target: docRefSchema,
		new_source: docRefSchema,
		output_filename: z
			.string()
			.nullish()
			.transform((value) => value ?? undefined),
	})
	.passthrough();

const parseConfig = (raw: unknown): SchemaExtractorPayload | null => {
	const parsed = schemaExtractorSchema.safeParse(raw);
	return parsed.success ? (parsed.data as SchemaExtractorPayload) : null;
};

const messageSchema = z.object({
	id: z.number(),
	role: z.string(),
	content_blocks: z.array(z.unknown()).default([]),
	created_at: z.number(),
	message_metadata: z
		.object({
			schema_extractor: z.unknown().optional(),
			streaming: z
				.object({ status: z.enum(["pending", "running"]) })
				.nullish(),
			stream_error: z.boolean().nullish(),
			/** Why a failed run failed, as the backend wrote it. */
			error_message: z.string().nullish(),
		})
		.default({}),
});

const messagesResponseSchema = z.object({
	chat: z.object({ id: z.string(), title: z.string() }),
	messages: z.array(messageSchema),
	next_cursor: z.string().nullish(),
	has_more: z.boolean().default(false),
});

const createChatResponseSchema = z.object({ id: z.string() });

const chatListRowSchema = z.object({
	id: z.string(),
	title: z.string(),
	last_message_at: z.number().nullable(),
	created_at: z.number(),
	latest_generation_status: z.enum(RUN_STATUSES).nullish(),
});

const chatListResponseSchema = z.object({
	chats: z.array(chatListRowSchema).default([]),
	next_cursor: z.string().nullish(),
	has_more: z.boolean().default(false),
});

const streamStartResponseSchema = z.object({
	generation_id: z.string(),
	assistant_message_id: z.number(),
});

/** The endpoint's own maximum, so a thread is read in as few requests as it can be. */
const RUN_PAGE_SIZE = 100;

/**
 * The most pages one read will follow — 50 runs' worth of turns per page, so
 * 1000 runs. A cap, not a budget: it exists so a cursor the server never stops
 * advancing cannot spin the page forever, and no real workflow reaches it.
 */
const MAX_RUN_PAGES = 20;

/** How many workflows one page of the index list asks for. */
const LIST_PAGE_SIZE = 100;

/**
 * The most pages the index will follow — 2000 workflows. Same cap-not-budget
 * reasoning as `MAX_RUN_PAGES`: a stop against a cursor the server never
 * retires, set far above any tenant we expect.
 */
const MAX_LIST_PAGES = 20;

export async function listLoanTapeDashboards(): Promise<
	LoanTapeDashboardSummary[]
> {
	// Every workflow, not the first hundred. The index is the only place a
	// workflow can be navigated to now, so one missing from this list is one
	// the user cannot reach — a partial list is not a shorter list, it is a
	// list with things silently gone from it.
	const readPage = (cursor: string | null) =>
		chatApi
			.listChats({ surface: "loan_tape", limit: LIST_PAGE_SIZE, cursor })
			.then((raw) => chatListResponseSchema.parse(raw));

	let page = await readPage(null);
	const chats = [...page.chats];
	for (let read = 1; read < MAX_LIST_PAGES && page.has_more; read += 1) {
		const cursor = page.next_cursor ?? null;
		// `has_more` without a cursor is a server that cannot tell us where to
		// continue; stopping is the only safe reading of it.
		if (!cursor) break;
		page = await readPage(cursor);
		chats.push(...page.chats);
	}

	return chats.map((chat) => ({
		id: chat.id,
		name: chat.title,
		last_run_ms: chat.last_message_at ?? null,
		created_at_ms: chat.created_at,
		status: chat.latest_generation_status ?? null,
	}));
}

/**
 * The message a run is sent as.
 *
 * The extractor routes on the metadata, not on these words, but the thread is
 * a real chat and someone may open it — so the turn reads as the instruction
 * it is. Kept in step with `handleSchemaExtractSubmit` in `pages/Chat.tsx`,
 * which composes the same sentence for the @-mention entry point.
 */
export function loanTapeRunMessage(payload: SchemaExtractorPayload): string {
	const stem = (name?: string) => (name ? name.replace(/\.[^.]+$/, "") : "");
	const newSource = payload.new_source.filename ?? "the selected workbook";
	const rawData = payload.training_source.filename ?? "the raw data file";
	const trainingTarget =
		payload.training_target.filename ?? "the training loan tape";
	const newTarget =
		payload.output_filename?.trim() ||
		(payload.new_source.filename
			? `${stem(payload.new_source.filename)} - Loan Tape.xlsx`
			: "the generated loan tape");
	return [
		`Run **Loan Tape Extraction** on **${newSource}**`,
		"",
		`- Map extraction and conversion of raw data into clean loan tape from **${rawData}** to **${trainingTarget}**`,
		`- Apply the mapped conversion to **${newTarget}**`,
	].join("\n");
}

/**
 * The chat was created; whether its first run started is unknown.
 *
 * Thrown instead of deleting, because the two failures are not the same event:
 * a refusal means the run never happened, but a timeout or a dropped response
 * means the request may well have committed a generation on the server. The
 * workflow exists either way and the id is carried so the caller can send the
 * user to it — a page that shows its own History is a better answer than a
 * deletion that might have thrown away a running extraction.
 */
export class LoanTapeCreateUnconfirmedError extends Error {
	constructor(readonly id: string) {
		super("The dashboard was created but its first run was not confirmed.");
		this.name = "LoanTapeCreateUnconfirmedError";
	}
}

/**
 * Did the server definitively REFUSE the run — as opposed to never answering?
 *
 * Only a 4xx that the request itself caused says the generation was not
 * committed. 408 and 429 are excluded: both mean "not now", not "not ever", and
 * a retry-after is no evidence about what the first attempt did. A network
 * error, a timeout and every 5xx leave the outcome unknown, and so does a
 * session expiry (which is not an `ApiError` at all) — none of them may delete.
 */
const isDefinitiveRefusal = (caught: unknown): boolean =>
	caught instanceof ApiError &&
	caught.status >= 400 &&
	caught.status < 500 &&
	caught.status !== 408 &&
	caught.status !== 429;

/**
 * What a workflow is called when its creator did not say. The new source is
 * the file the workflow exists to transform, so its name is the honest one.
 */
export function defaultLoanTapeName(payload: SchemaExtractorPayload): string {
	const filename = payload.new_source.filename;
	const stem = filename ? filename.replace(/\.[^.]+$/, "") : "";
	return stem ? `${stem} — Loan Tape` : "Loan Tape";
}

/** Start a run on an existing workflow with the config it is given. */
export async function runLoanTapeDashboard(
	id: string,
	payload: SchemaExtractorPayload,
): Promise<void> {
	const raw = await apiService.post("/api/v3/chats/stream/start", {
		message: loanTapeRunMessage(payload),
		chat_id: id,
		message_metadata: {
			attachments: [],
			document_refs: [],
			schema_extractor: payload,
		},
	});
	streamStartResponseSchema.parse(raw);
}

/**
 * Create the workflow and start its first run.
 *
 * Two calls, in this order, because the run needs a chat to stream into. The
 * chat id is returned as soon as it exists so the caller can navigate to the
 * page and watch the first run arrive there rather than on the modal.
 */
export async function createLoanTapeDashboard(
	name: string,
	payload: SchemaExtractorPayload,
): Promise<string> {
	const raw = await apiService.post("/api/v3/chats", {
		surface: "loan_tape",
		title: name,
		chat_type: null,
	});
	const { id } = createChatResponseSchema.parse(raw);
	try {
		await runLoanTapeDashboard(id, payload);
	} catch (caught) {
		// The chat and the first run are one act to the user, so a REFUSED run
		// must not leave a named `loan_tape` chat behind: it would show up on
		// /workflows as a workflow that has never run and cannot run, from a
		// create the user was told had failed. Best effort — if the cleanup is
		// refused too, the original failure is still what gets reported.
		if (isDefinitiveRefusal(caught)) {
			await deleteLoanTapeDashboard(id).catch(() => undefined);
			throw caught;
		}
		// Anything else and we do not know what happened to the run. Deleting on
		// a timeout would destroy a workflow whose extraction is running.
		throw new LoanTapeCreateUnconfirmedError(id);
	}
	return id;
}

/**
 * Delete the workflow, which is to say the chat it is made of. There is
 * nothing else to remove: the blocks are the chat's messages and the workbook
 * bytes hang off them.
 */
export async function deleteLoanTapeDashboard(id: string): Promise<void> {
	await chatApi.deleteChat(id);
}

/**
 * A run's status, from the answer that belongs to it.
 *
 * `streaming` is present only while the generation is open, and
 * `stream_error` is how a failure survives the stream ending — so the two of
 * them, plus "there is an answer at all", cover every state without a second
 * request. A question with no answer yet is a run that has not started
 * streaming, which reads as pending.
 */
function runStatus(
	answer: z.infer<typeof messageSchema> | undefined,
): LoanTapeRunStatus {
	if (!answer) return "pending";
	if (answer.message_metadata.stream_error) return "failed";
	const streaming = answer.message_metadata.streaming;
	if (streaming) return streaming.status;
	return "completed";
}

/** One page of a thread, newest message first. */
async function readMessagePage(id: string, cursor: string | null) {
	const params = new URLSearchParams({ limit: String(RUN_PAGE_SIZE) });
	if (cursor) params.set("cursor", cursor);
	const response = await apiFetch(
		`/api/v3/chats/${encodeURIComponent(id)}/messages?${params.toString()}`,
		{ method: "GET" },
	);
	if (!response.ok) {
		throw new Error(`Failed to load the dashboard: ${response.status}`);
	}
	return messagesResponseSchema.parse(await response.json());
}

export async function getLoanTapeDashboard(
	id: string,
): Promise<LoanTapeDashboard> {
	// The WHOLE thread, not the first page.
	//
	// Two things break on a partial read, and the second is the bad one:
	// History silently loses the older runs, and — because a page boundary can
	// fall BETWEEN a question and the answer to it — the newest run can arrive
	// without its answer and read as pending with no blocks. A workflow that
	// says "still running" about a run that finished is worse than a slow one.
	let cursor: string | null = null;
	let page = await readMessagePage(id, cursor);
	const chat = page.chat;
	const messages = [...page.messages];
	for (let read = 1; read < MAX_RUN_PAGES && page.has_more; read += 1) {
		cursor = page.next_cursor ?? null;
		// `has_more` without a cursor is a server that cannot tell us where to
		// continue; stopping is the only safe reading of it.
		if (!cursor) break;
		page = await readMessagePage(id, cursor);
		messages.push(...page.messages);
	}

	// The endpoint pages newest-first; a run is read as a pair, so read the
	// thread forwards and pair each question with the answer that follows it.
	// Positional pairing is sound HERE and only here: nothing but a workflow
	// run is ever sent to a `loan_tape` thread, so a question's answer is the
	// next message. (An ordinary chat needs `answers_message_id`, because a
	// regenerate breaks the position.)
	const ordered = [...messages].reverse();

	const runs: LoanTapeRun[] = [];
	ordered.forEach((message, index) => {
		if (message.role !== "user") return;
		const answer =
			ordered[index + 1]?.role !== "user" ? ordered[index + 1] : undefined;
		runs.push({
			id: message.id,
			started_at_ms: message.created_at,
			status: runStatus(answer),
			error: answer?.message_metadata.stream_error
				? (answer.message_metadata.error_message ?? null)
				: null,
			config: parseConfig(message.message_metadata.schema_extractor),
			blocks: (answer?.content_blocks ?? [])
				.map(parseContentBlock)
				.filter((block): block is ContentBlock => block !== null),
		});
	});
	runs.reverse();

	return {
		id: chat.id,
		title: chat.title,
		// The newest run that carried one: an ordinary chat turn on the thread
		// must not erase the configuration the workflow runs on.
		config: runs.find((run) => run.config !== null)?.config ?? null,
		runs,
		latest: runs[0] ?? null,
	};
}

// --- reading one run's blocks ----------------------------------------------

const lastBlock = <T extends ContentBlock>(
	blocks: ContentBlock[],
	type: T["type"],
): T | null => {
	for (let i = blocks.length - 1; i >= 0; i -= 1) {
		if (blocks[i].type === type) return blocks[i] as T;
	}
	return null;
};

export const previewBlockOf = (run: LoanTapeRun | null) =>
	run ? lastBlock<LoanTapePreviewBlock>(run.blocks, "loan_tape_preview") : null;

export const workbookBlockOf = (run: LoanTapeRun | null) =>
	run ? lastBlock<ModifiedWorkbookBlock>(run.blocks, "modified_workbook") : null;

export const schemaGraphOf = (run: LoanTapeRun | null) =>
	run ? lastBlock<SchemaGraphBlock>(run.blocks, "schema_graph") : null;

/** The workbook this run produced, for the meta line and the history row. */
export const outputFilenameOf = (run: LoanTapeRun | null): string | null =>
	workbookBlockOf(run)?.filename ?? run?.config?.output_filename ?? null;

// --- choosing workbooks (DOP-627) ------------------------------------------

/** The extractor reads `.xlsx` only, so nothing else is offered. */
const isWorkbook = (doc: CatalogDocument): boolean =>
	doc.ref_type !== "deal" &&
	doc.ref_type !== "template" &&
	/\.xlsx$/i.test(doc.filename);

/**
 * Workbooks in the Deal Room the extractor can read, matching `query`.
 *
 * The same catalog mention-search the chat's @ uses, so a file is offered here
 * exactly when the extractor can resolve it. `projectId` narrows the search to
 * one deal: the new file is looked for beside the example the tape copies.
 */
export async function searchLoanTapeWorkbooks(
	query: string,
	options: { projectId?: string | null; limit?: number } = {},
): Promise<CatalogDocument[]> {
	const params = new URLSearchParams({ limit: String(options.limit ?? 20) });
	const q = query.trim();
	if (q) params.set("q", q);
	if (options.projectId) params.set("project_id", options.projectId);
	const docs = await apiGet<CatalogDocument[]>(
		`/api/v3/catalog/mention-search?${params.toString()}`,
	);
	return (Array.isArray(docs) ? docs : []).filter(isWorkbook);
}
